import { useEffect, useRef, useState } from "react";
import axios from "axios";
import {
  Button,
  Col,
  Form,
  Input,
  message,
  Popconfirm,
  Row,
  Table,
  Tag,
  Tooltip,
} from "antd";

import {
  DeleteOutlined,
  EditOutlined,
  EnvironmentOutlined,
  PlusCircleOutlined,
  ReloadOutlined,
  SaveOutlined,
  SearchOutlined,
  CloseCircleOutlined,
  GlobalOutlined,
  NumberOutlined,
  UnorderedListOutlined,
  FormOutlined,
} from "@ant-design/icons";
import { CREATE_jwel } from "../../Config/Config";

const tenantName = localStorage.getItem("tenantName");

/* ─── palette ───────────────────────────────────────────────── */
const PURPLE = "#7c3aed";
const INDIGO = "#4f46e5";
const TEAL = "#0d9488";
const AMBER = "#d97706";
const ROSE = "#e11d48";
const EMERALD = "#059669";
const SKY = "#0284c7";

const G = {
  page: {
    background: "linear-gradient(135deg,#f5f3ff 0%,#eff6ff 40%,#ecfdf5 100%)",
    height: "calc(100vh - 105px)",
    padding: "20px 24px",
  },

  /* header bar */
  headerBar: {
    display: "flex",
    alignItems: "center",
    justifyContent: "space-between",
    flexWrap: "wrap",
    gap: 12,
    marginBottom: 16,
  },
  headerLeft: { display: "flex", alignItems: "center", gap: 14 },
  headerIcon: {
    width: 52,
    height: 52,
    borderRadius: 14,
    background: `linear-gradient(135deg,${PURPLE} 0%,${INDIGO} 100%)`,
    display: "flex",
    alignItems: "center",
    justifyContent: "center",
    fontSize: 26,
    color: "#fff",
    boxShadow: `0 6px 18px ${PURPLE}55`,
  },
  headerTitle: {
    margin: 0,
    fontSize: 22,
    fontWeight: 800,
    background: `linear-gradient(90deg,${PURPLE},${INDIGO})`,
    WebkitBackgroundClip: "text",
    WebkitTextFillColor: "transparent",
    letterSpacing: 0.3,
  },
  headerSub: { margin: 0, fontSize: 13, color: "#64748b", marginTop: 2 },

  /* stats row */
  statsRow: { display: "flex", gap: 12, flexWrap: "wrap" },
  stat: (color) => ({
    background: "#fff",
    borderRadius: 12,
    padding: "10px 18px",
    display: "flex",
    flexDirection: "column",
    alignItems: "center",
    boxShadow: "0 2px 10px rgba(0,0,0,0.07)",
    borderTop: `3px solid ${color}`,
    minWidth: 80,
  }),
  statNum: (color) => ({
    fontSize: 22,
    fontWeight: 800,
    color,
    lineHeight: 1.1,
  }),
  statLabel: {
    fontSize: 11,
    color: "#94a3b8",
    fontWeight: 600,
    marginTop: 2,
    textTransform: "uppercase",
    letterSpacing: 0.5,
  },

  card: {
    background: "#fff",
    borderRadius: 18,
    boxShadow: "0 4px 28px rgba(0,0,0,0.09)",
    overflow: "hidden",
    display: "flex",
    flexDirection: "column",
    minHeight: 0,
  },

  /* panel header */
  panelHead: (from, to) => ({
    background: `linear-gradient(120deg,${from} 0%,${to} 100%)`,
    padding: "14px 20px",
    display: "flex",
    alignItems: "center",
    justifyContent: "space-between",
    gap: 12,
  }),
  panelTitle: {
    display: "flex",
    alignItems: "center",
    gap: 8,
    color: "#fff",
    fontWeight: 700,
    fontSize: 14,
    letterSpacing: 0.4,
    textTransform: "uppercase",
  },
  panelBody: { padding: "22px 22px 18px" },

  /* form input */
  inputStyle: { height: 42, borderRadius: 10, fontSize: 14 },

  /* section label */
  sectionLabel: (color) => ({
    display: "flex",
    alignItems: "center",
    gap: 7,
    fontSize: 11,
    fontWeight: 700,
    color,
    textTransform: "uppercase",
    letterSpacing: 0.8,
    marginBottom: 12,
    marginTop: 4,
  }),
  dot: (color) => ({
    width: 8,
    height: 8,
    borderRadius: "50%",
    background: color,
    flexShrink: 0,
  }),

  /* selected row hint */
  hint: {
    background: "#f8fafc",
    border: "1px dashed #cbd5e1",
    borderRadius: 10,
    padding: "10px 14px",
    fontSize: 12,
    color: "#94a3b8",
    display: "flex",
    alignItems: "center",
    gap: 8,
    marginBottom: 16,
  },
  selectedBadge: (color) => ({
    background: `${color}18`,
    border: `1px solid ${color}44`,
    borderRadius: 10,
    padding: "8px 14px",
    display: "flex",
    alignItems: "center",
    gap: 10,
    marginBottom: 16,
  }),
  badgeAvatar: (color) => ({
    width: 36,
    height: 36,
    borderRadius: 10,
    background: `linear-gradient(135deg,${color} 0%,${color}99 100%)`,
    display: "flex",
    alignItems: "center",
    justifyContent: "center",
    color: "#fff",
    fontSize: 18,
    flexShrink: 0,
  }),

  /* search bar */
  searchWrap: {
    display: "flex",
    alignItems: "center",
    gap: 8,
    background: "rgba(255,255,255,0.15)",
    borderRadius: 20,
    padding: "4px 10px 4px 4px",
  },

  /* table row striping handled by rowClassName */
};

/* ─── S.No badge colours round-robin ────────────────────────── */
const ROW_COLORS = [PURPLE, SKY, TEAL, EMERALD, AMBER, ROSE, INDIGO];

export default function AreaMaster() {
  const [form] = Form.useForm();
  const [tableData, setTableData] = useState([]);
  const [loading, setLoading] = useState(false);
  const [saving, setSaving] = useState(false);
  const [editingKey, setEditingKey] = useState(null); // null = add mode
  const [filter, setFilter] = useState("");

  const areaNameRef = useRef(null);

  /* ── fetch ───────────────────────────────────────────────── */
  const fetchData = async () => {
    setLoading(true);
    try {
      const res = await axios.get(
        `${CREATE_jwel}/api/ERP/GetDataFromGivenTableName`,
        { params: { tableName: "area_mast" }, headers: { tenantName } },
      );
      const rows = (res.data || []).map((item, idx) => ({
        key: idx,
        areacode: item.Areacode ?? item.AREACODE ?? "-",
        areaname: item.Areaname ?? item.AREANAME ?? "-",
        clouD_UPLOAD: item.CLOUD_UPLOAD ?? item.clouD_UPLOAD ?? "-",
      }));
      setTableData(rows);
    } catch {
      message.error("Failed to load area data");
    } finally {
      setLoading(false);
    }
  };

  useEffect(() => {
    fetchData();
    setTimeout(() => areaNameRef.current?.focus(), 300);
  }, []); // eslint-disable-line react-hooks/exhaustive-deps

  /* ── derived state ───────────────────────────────────────── */
  const q = filter.trim().toLowerCase();
  const filteredData = q
    ? tableData.filter(
        (r) =>
          r.areaname?.toLowerCase().includes(q) ||
          r.areacode?.toLowerCase().includes(q),
      )
    : tableData;

  const editingRecord =
    editingKey != null ? tableData.find((r) => r.key === editingKey) : null;

  /* ── handlers ────────────────────────────────────────────── */
  const handleEdit = (record) => {
    setEditingKey(record.key);
    form.setFieldsValue({
      areaname: record.areaname === "-" ? "" : record.areaname,
      areacode: record.areacode === "-" ? "" : record.areacode,
    });
    setTimeout(() => areaNameRef.current?.focus(), 100);
  };

  const handleCancel = () => {
    form.resetFields();
    setEditingKey(null);
    setFilter("");
    setTimeout(() => areaNameRef.current?.focus(), 100);
  };

  const handleRefresh = () => {
    form.resetFields();
    setEditingKey(null);
    setFilter("");
    fetchData();
    setTimeout(() => areaNameRef.current?.focus(), 300);
  };

  const deleteArea = (areaname) => {
    const safeName = String(areaname).replace(/'/g, "''"); // escape apostrophes
    return axios.post(
      `${CREATE_jwel}/api/ERP/DeleteDataFromGivenTableNameWithWhere?tableName=area_mast&where=${encodeURIComponent(`AREANAME='${safeName}'`)}`,
      {},
      { headers: { tenantName } },
    );
  };

  const handleSubmit = async (values) => {
    setSaving(true);
    try {
      const payload = {
        areacode: values.areacode?.trim() || "-",
        areaname: (values.areaname || "").trim().toUpperCase(),
        clouD_UPLOAD: "true",
      };

      // Update = delete the old record first, then insert fresh
      if (editingKey != null) {
        const oldRecord = tableData.find((r) => r.key === editingKey);
        if (oldRecord) {
          await deleteArea(oldRecord.areaname);
        }
      }

      await axios.post(`${CREATE_jwel}/api/Master/AreaMast`, payload, {
        headers: { tenantName },
      });

      message.success(
        editingKey != null
          ? "Area updated successfully"
          : "Area added successfully",
        2,
      );
      handleCancel();
      fetchData();
    } catch (err) {
      message.error(err?.response?.data?.message || "Failed to save area");
    } finally {
      setSaving(false);
    }
  };

  const handleDelete = async (record) => {
    try {
      await deleteArea(record.areaname);
      message.success("Area deleted");
      fetchData();
    } catch (err) {
      console.error("Delete failed:", err.response?.data || err.message);
      message.error("Delete failed");
    }
  };

  /* ── columns ─────────────────────────────────────────────── */
  const columns = [
    {
      title: "#",
      width: 58,
      align: "center",
      render: (_, __, idx) => {
        const color = ROW_COLORS[idx % ROW_COLORS.length];
        return (
          <span
            style={{
              display: "inline-flex",
              alignItems: "center",
              justifyContent: "center",
              width: 28,
              height: 28,
              borderRadius: 8,
              background: `${color}18`,
              color,
              fontWeight: 700,
              fontSize: 12,
            }}
          >
            {idx + 1}
          </span>
        );
      },
    },
    {
      title: (
        <span
          style={{
            display: "flex",
            alignItems: "center",
            gap: 5,
            color: PURPLE,
          }}
        >
          <EnvironmentOutlined /> Area Name
        </span>
      ),
      dataIndex: "areaname",
      render: (v) => (
        <span style={{ fontWeight: 600, color: "#1e293b", fontSize: 13 }}>
          {v}
        </span>
      ),
    },
    {
      title: (
        <span
          style={{ display: "flex", alignItems: "center", gap: 5, color: SKY }}
        >
          <NumberOutlined /> Area Code
        </span>
      ),
      dataIndex: "areacode",
      width: 120,
      render: (v) =>
        v && v !== "-" ? (
          <Tag
            style={{
              background: `${SKY}15`,
              color: SKY,
              border: `1px solid ${SKY}44`,
              borderRadius: 6,
              fontWeight: 600,
              fontSize: 12,
            }}
          >
            {v}
          </Tag>
        ) : (
          <span style={{ color: "#cbd5e1", fontSize: 12 }}>—</span>
        ),
    },
    {
      title: "Actions",
      width: 100,
      align: "center",
      render: (_, record) => (
        <div style={{ display: "flex", gap: 6, justifyContent: "center" }}>
          <Tooltip title="Edit">
            <Button
              size="small"
              icon={<EditOutlined />}
              onClick={() => handleEdit(record)}
              style={{
                background: `${INDIGO}15`,
                color: INDIGO,
                border: `1px solid ${INDIGO}33`,
                borderRadius: 8,
              }}
            />
          </Tooltip>
          <Popconfirm
            title="Delete this area?"
            description="This action cannot be undone."
            onConfirm={() => handleDelete(record)}
            okText="Delete"
            cancelText="Cancel"
            okButtonProps={{ danger: true }}
          >
            <Tooltip title="Delete">
              <Button
                size="small"
                icon={<DeleteOutlined />}
                style={{
                  background: `${ROSE}15`,
                  color: ROSE,
                  border: `1px solid ${ROSE}33`,
                  borderRadius: 8,
                }}
              />
            </Tooltip>
          </Popconfirm>
        </div>
      ),
    },
  ];

  /* ── render ──────────────────────────────────────────────── */
  return (
    <div style={G.page}>
      {/* ── Page Header ─────────────────────────────────────── */}
      <div style={G.headerBar}>
        <div style={G.headerLeft}>
          <div style={G.headerIcon}>
            <GlobalOutlined />
          </div>
          <div>
            <h1 style={G.headerTitle}>Area Master</h1>
            <p style={G.headerSub}>Manage geographic areas and locations</p>
          </div>
        </div>

        {/* Stats */}
        <div style={G.statsRow}>
          <div style={G.stat(PURPLE)}>
            <span style={G.statNum(PURPLE)}>{tableData.length}</span>
            <span style={G.statLabel}>Total</span>
          </div>
          <div style={G.stat(SKY)}>
            <span style={G.statNum(SKY)}>{filteredData.length}</span>
            <span style={G.statLabel}>Filtered</span>
          </div>
          <div style={G.stat(TEAL)}>
            <span style={G.statNum(TEAL)}>
              {tableData.filter((r) => r.areacode && r.areacode !== "-").length}
            </span>
            <span style={G.statLabel}>Coded</span>
          </div>
        </div>
      </div>

      {/* ── Two-panel layout ─────────────────────────────────── */}
      <Row gutter={[20, 20]} align="top">
        {/* ── Table Panel ──────────────────────────────────── */}
        <Col xs={24} xl={16}>
          <div style={G.card}>
            <div style={G.panelHead(SKY, INDIGO)}>
              <span style={G.panelTitle}>
                <UnorderedListOutlined style={{ fontSize: 16 }} />
                All Areas
              </span>

              {/* Search */}
              <div
                style={{
                  display: "flex",
                  alignItems: "center",
                  background: "rgba(255,255,255,0.18)",
                  borderRadius: 20,
                  padding: "3px 12px 3px 8px",
                  gap: 6,
                  minWidth: 220,
                }}
              >
                <SearchOutlined
                  style={{ color: "rgba(255,255,255,0.8)", fontSize: 14 }}
                />
                <input
                  className="am-search"
                  value={filter}
                  onChange={(e) => setFilter(e.target.value)}
                  placeholder="Search area name or code…"
                  style={{
                    background: "transparent",
                    border: "none",
                    outline: "none",
                    color: "#fff",
                    fontSize: 13,
                    width: "100%",
                  }}
                />
                {filter && (
                  <CloseCircleOutlined
                    style={{
                      color: "rgba(255,255,255,0.7)",
                      cursor: "pointer",
                      fontSize: 13,
                    }}
                    onClick={() => setFilter("")}
                  />
                )}
              </div>
            </div>

            <div style={{ padding: "4px 0" }}>
              <Table
                columns={columns}
                dataSource={filteredData}
                loading={loading}
                size="middle"
                pagination={false} // ← pagination removed
                scroll={{ x: 600, y: "calc(100vh - 320px)" }} // ← only the table body scrolls
                rowClassName={(record) =>
                  record.key === editingKey ? "am-row-selected" : "am-row"
                }
                onRow={(record) => ({
                  onClick: () => handleEdit(record),
                  style: { cursor: "pointer" },
                })}
              />
            </div>
          </div>
        </Col>

        {/* ── Form Panel ───────────────────────────────────── */}
        <Col xs={24} xl={8}>
          <div style={G.card}>
            <div style={G.panelHead(PURPLE, INDIGO)}>
              <span style={G.panelTitle}>
                <FormOutlined style={{ fontSize: 16 }} />
                {editingKey != null ? "Edit Area" : "Add New Area"}
              </span>
              {editingKey != null && (
                <Tooltip title="Cancel editing">
                  <button
                    type="button"
                    onClick={handleCancel}
                    style={{
                      background: "rgba(255,255,255,0.2)",
                      border: "none",
                      color: "#fff",
                      borderRadius: 8,
                      width: 28,
                      height: 28,
                      cursor: "pointer",
                      display: "flex",
                      alignItems: "center",
                      justifyContent: "center",
                      fontSize: 14,
                    }}
                  >
                    <CloseCircleOutlined />
                  </button>
                </Tooltip>
              )}
            </div>

            <div style={G.panelBody}>
              {/* Context badge */}
              {editingKey == null ? (
                <div style={G.hint}>
                  <PlusCircleOutlined style={{ color: PURPLE, fontSize: 15 }} />
                  <span>Fill in the details below to add a new area.</span>
                </div>
              ) : (
                editingRecord && (
                  <div style={G.selectedBadge(PURPLE)}>
                    <div style={G.badgeAvatar(PURPLE)}>
                      <EnvironmentOutlined />
                    </div>
                    <div>
                      <div
                        style={{
                          fontWeight: 700,
                          color: "#1e293b",
                          fontSize: 13,
                        }}
                      >
                        {editingRecord.areaname}
                      </div>
                      <div
                        style={{ fontSize: 11, color: "#64748b", marginTop: 2 }}
                      >
                        Code: {editingRecord.areacode}
                      </div>
                    </div>
                  </div>
                )
              )}

              <Form
                form={form}
                layout="vertical"
                onFinish={handleSubmit}
                requiredMark={false}
              >
                {/* Section label */}
                <div style={G.sectionLabel(INDIGO)}>
                  <span style={G.dot(INDIGO)} />
                  Area Details
                </div>

                {/* Area Name */}
                <Form.Item
                  label={
                    <span
                      style={{
                        fontWeight: 600,
                        color: "#374151",
                        fontSize: 13,
                      }}
                    >
                      Area Name <span style={{ color: ROSE }}>*</span>
                    </span>
                  }
                  name="areaname"
                  rules={[
                    { required: true, message: "Area name is required" },
                    { min: 2, message: "At least 2 characters" },
                  ]}
                >
                  <Input
                    ref={areaNameRef}
                    placeholder="e.g. BANGALORE"
                    prefix={
                      <EnvironmentOutlined
                        style={{ color: PURPLE, fontSize: 15 }}
                      />
                    }
                    style={G.inputStyle}
                    onKeyDown={(e) => {
                      if (e.key === "Enter") {
                        e.preventDefault();
                        form.submit();
                      }
                    }}
                  />
                </Form.Item>

                {/* Area Code */}
                <Form.Item
                  label={
                    <span
                      style={{
                        fontWeight: 600,
                        color: "#374151",
                        fontSize: 13,
                      }}
                    >
                      Area Code
                    </span>
                  }
                  name="areacode"
                >
                  <Input
                    placeholder="e.g. BLR"
                    prefix={
                      <NumberOutlined style={{ color: SKY, fontSize: 15 }} />
                    }
                    style={G.inputStyle}
                    onKeyDown={(e) => {
                      if (e.key === "Enter") {
                        e.preventDefault();
                        form.submit();
                      }
                    }}
                  />
                </Form.Item>

                {/* Buttons */}
                <div
                  style={{
                    display: "flex",
                    gap: 10,
                    marginTop: 6,
                    flexWrap: "wrap",
                  }}
                >
                  <Button
                    type="primary"
                    htmlType="submit"
                    loading={saving}
                    icon={
                      editingKey != null ? (
                        <SaveOutlined />
                      ) : (
                        <PlusCircleOutlined />
                      )
                    }
                    style={{
                      background: `linear-gradient(90deg,${PURPLE} 0%,${INDIGO} 100%)`,
                      border: "none",
                      borderRadius: 10,
                      height: 42,
                      fontWeight: 700,
                      fontSize: 13,
                      flex: 1,
                      boxShadow: `0 4px 14px ${PURPLE}44`,
                    }}
                  >
                    {editingKey != null ? "Update Area" : "Add Area"}
                  </Button>

                  {editingKey != null && (
                    <Button
                      onClick={handleCancel}
                      icon={<CloseCircleOutlined />}
                      style={{
                        borderRadius: 10,
                        height: 42,
                        fontWeight: 600,
                        border: `1px solid ${ROSE}44`,
                        color: ROSE,
                        background: `${ROSE}08`,
                      }}
                    >
                      Cancel
                    </Button>
                  )}

                  <Tooltip title="Refresh — resets all state">
                    <Button
                      icon={<ReloadOutlined />}
                      onClick={handleRefresh}
                      loading={loading}
                      style={{
                        borderRadius: 10,
                        height: 42,
                        border: `1px solid ${TEAL}44`,
                        color: TEAL,
                        background: `${TEAL}08`,
                      }}
                    />
                  </Tooltip>
                </div>
              </Form>
            </div>
          </div>

          {/* Quick legend */}
          <div
            style={{
              marginTop: 16,
              background: "#fff",
              borderRadius: 14,
              padding: "14px 18px",
              boxShadow: "0 2px 10px rgba(0,0,0,0.06)",
            }}
          >
            <div
              style={{
                fontSize: 11,
                fontWeight: 700,
                color: "#94a3b8",
                textTransform: "uppercase",
                letterSpacing: 0.7,
                marginBottom: 10,
              }}
            >
              Quick Tips
            </div>
            {[
              { color: PURPLE, text: "Click any row in the table to edit it" },
              { color: TEAL, text: "Press Enter to jump between fields" },
              { color: AMBER, text: "Area names are saved in uppercase" },
            ].map((tip, i) => (
              <div
                key={i}
                style={{
                  display: "flex",
                  alignItems: "flex-start",
                  gap: 8,
                  marginBottom: 7,
                  fontSize: 12,
                  color: "#475569",
                }}
              >
                <span
                  style={{
                    width: 8,
                    height: 8,
                    borderRadius: "50%",
                    background: tip.color,
                    flexShrink: 0,
                    marginTop: 4,
                  }}
                />
                {tip.text}
              </div>
            ))}
          </div>
        </Col>
      </Row>

      {/* Inline styles for row hover / selection */}
      <style>{`
        .am-row:hover td { background: #f5f3ff !important; transition: background 0.15s; }
        .am-row-selected td { background: #ede9fe !important; }
        .am-row-selected:hover td { background: #ddd6fe !important; }

        .am-search::placeholder { color: rgba(255,255,255,0.8); opacity: 1; }
        .am-search::-webkit-input-placeholder { color: rgba(255,255,255,0.8); opacity: 1; }
      `}</style>
    </div>
  );
}
